import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { createSelector, select, Store } from '@ngrx/store';
import { toLower } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { ApplicationConfigs } from 'src/app/core/application-config/application-config.state';
import { selectTenantName } from 'src/app/core/client-configuration/client-configuration.selectors';
import { selectEnvironment } from 'src/app/core/environment/environment.selectors';
import { EnvironmentState } from 'src/app/core/environment/environment.state';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { routeTo } from 'src/app/core/routing/routing.actions';
import { logout } from 'src/app/core/user/user.actions';
import { User } from 'src/app/core/user/user.models';
import {
	selectContractViewerDetails,
	selectIsLockoutFlow,
	selectIsMembership,
	selectOrderTicketTypes,
	selectTokens,
	selectUser,
	Tokens,
} from 'src/app/core/user/user.selectors';
import { OrderTicketType } from 'src/app/core/user/user.state';
import { dayjs } from 'src/app/dayjs.lib';
import {
	APPCONFIG_BOOKINGS,
	APPCONFIG_CONTRACT_VIEWER,
	APPCONFIG_DISABLE_FAQ_TAB,
	APPCONFIG_DISABLE_MEMBERSHIP_TAB,
	APPCONFIG_DISABLE_PAYMENT_HISTORY_TAB,
	APPCONFIG_NAVIGATION_TREE_DISPLAY,
	APPCONFIG_PRIMARY_NAV_COLOR,
	APPCONFIG_PRIMARY_NAV_COLOR_TEXT,
	APPCONFIG_RECEIPTS,
	APPCONFIG_SUPPORTED_LANGUAGES,
} from 'src/app/shared/enums/application-config.enums';
import { DEFAULT_NAV_BACKGROUND_COLOR, DEFAULT_NAV_FOREGROUND_COLOR } from 'src/app/shared/enums/default-configs.enums';
import { validArray, validString } from 'src/app/shared/utilities/types.utils';
import { selectSessionId } from '../../../../core/session/session.selectors';
import { ACCESSO_PASSPORT_LOGO_ALT_DEFAULT } from '../../footer/login-footer/login-footer.component';

export interface NavItem {
	type: 'button' | 'dropdown';
	label?: string;
	dropDownName?: string;
	clickAction?: 'routeTo' | 'navigateTo' | 'logout';
	clickActionPayload?: string | string[];
	displayIf: boolean;
	analyticsClick?: string;
	routeEnd?: string;
	buttons?: NavItem[];
	gapOpen?: boolean;
	id: string;
}
export interface GapOpenState {
	payments: boolean;
	documents: boolean;
}

@Component({
	selector: 'ahp-side-navigation',
	templateUrl: './side-navigation.component.html',
	styleUrls: ['./side-navigation.component.scss'],
})
export class SideNavigationComponent implements OnDestroy, OnChanges, OnInit {
	/**
	 * input of boolean for whether the mobile menu is open or not
	 */
	@Input()
	public isMobileOpen = false;
	/**
	 * input of boolean for whether this is the main nav or not
	 */
	@Input()
	public isMainNav = false;
	/**
	 * emits when the mobile nav should be closed
	 */
	@Output()
	public closeMobile = new EventEmitter();
	/**
	 * Build version
	 */
	public buildVersion: string = require('../../../../../../package.json').version;
	/**
	 * Display year from the build date
	 */
	buildTimestamp = dayjs().format('YYYY');
	/**
	 * Localized text for accesso Passport logo
	 */
	public accessoLogoAltText: string;
	/**
	 * the current sessionId
	 */
	public sessionId: string;
	/**
	 * Is the current order a membership?
	 */
	public isMembership: boolean;
	/**
	 * Does the current order have payment history?
	 */
	public hasPaymentHistory: boolean;
	/**
	 * App config to hide the receipts navigation item
	 */
	public disableReceiptsNavItem: boolean;
	/**
	 * App config to disable the faq option
	 */
	public disableFaqTab: boolean;
	/**
	 * App config to disable the membership option
	 */
	public disableMembershipTab: boolean;
	/**
	 * App config to disable the payment history option
	 */
	public disablePaymentHistoryTab: boolean;
	/**
	 * App config to disable the manage payments option
	 */
	public disableManagePaymentsTab: boolean;
	/**
	 * App config to enable the booking portal option
	 */
	public enableBookingPortalTab: boolean;
	/**
	 * App config for navigation bar background color
	 */
	public primaryNavColor: string;
	/**
	 * App config for navigation bar foreground color
	 */
	public primaryNavTextColor: string;
	/**
	 * Legacy URL for Contract Viewer that opens in a new tab outside the application
	 */
	public contractViewerUrl: string;
	/**
	 * Flag to determine if New Contract Viewer that displays inside the application or Legacy URL should be used
	 */
	public showContractViewerComponent: boolean;
	/**
	 * App config for the receipt viewer url
	 */
	public receiptUrl: string;
	/**
	 * The nav items for this menu
	 */
	public navItems: Array<NavItem>;
	/**
	 * The order navItems should be displayed in
	 */
	public navigationTreeDisplay: Array<string>;
	/**
	 * order ticket types for the current order
	 * note: payment plan defaults to true, all others false
	 */
	public orderTicketTypes: OrderTicketType = {
		isPaymentPlan: true,
		isBookable: false,
		isLoqBookable: false,
		isMembership: false,
		isNewContractViewer: false,
	};
	/**
	 * the gap open state for the nav menu
	 */
	public gapOpenState: GapOpenState = {
		payments: false,
		documents: false,
	};
	/**
	 * Languages options supported by merchant
	 */
	public supportedLanguages: string[];
	/**
	 * For unsubscribing to observables when the component destroys.
	 * @private
	 */
	private _destroy$ = new Subject<void>();
	/**
	 * Our constructor
	 * @param _store$ The global store
	 * @param _router the router
	 * @param _locale the locale service
	 */
	constructor(private _store$: Store<AppState>, private _router: Router, private _locale: LocaleService) {
		const logoAltTextLocale = 'accessibility.accessopassportlogoalttext';
		this.accessoLogoAltText = this._locale.has(logoAltTextLocale)
			? this._locale.get(logoAltTextLocale)
			: ACCESSO_PASSPORT_LOGO_ALT_DEFAULT;

		this._store$
			.pipe(
				select(
					createSelector(
						selectUser,
						selectEnvironment,
						selectAppConfig([
							APPCONFIG_CONTRACT_VIEWER,
							APPCONFIG_RECEIPTS,
							APPCONFIG_PRIMARY_NAV_COLOR,
							APPCONFIG_PRIMARY_NAV_COLOR_TEXT,
							APPCONFIG_DISABLE_FAQ_TAB,
							APPCONFIG_DISABLE_MEMBERSHIP_TAB,
							APPCONFIG_DISABLE_PAYMENT_HISTORY_TAB,
							APPCONFIG_BOOKINGS,
							APPCONFIG_SUPPORTED_LANGUAGES,
							APPCONFIG_NAVIGATION_TREE_DISPLAY,
						]),
						selectTokens,
						selectTenantName,
						selectIsLockoutFlow,
						selectIsMembership,
						selectSessionId,
						selectOrderTicketTypes,
						(
							userState,
							env,
							configs,
							tokens,
							tenant,
							isLockout,
							isMembership,
							sessionId,
							orderTicketType
						) => {
							const { user } = userState;
							return {
								env,
								configs,
								tenant,
								tokens,
								user,
								isLockout,
								isMembership,
								sessionId,
								orderTicketType,
							};
						}
					)
				),
				takeUntil(this._destroy$)
			)
			.subscribe((data) => {
				const { user, env, tenant, tokens, configs, sessionId } = data;
				this.hasPaymentHistory = validArray(user?.paymentHistory);
				this.sessionId = sessionId;

				this.isMembership = data.isMembership;
				this.disableReceiptsNavItem = configs?.receipts?.disable_receipts_nav_item;
				this.disableMembershipTab = configs?.disable_membership_tab;
				this.disableFaqTab = configs?.disable_faq_tab;
				this.disablePaymentHistoryTab = configs?.disable_payment_history_tab;
				this.disableManagePaymentsTab = data.isLockout;
				this.enableBookingPortalTab = configs.bookings?.enableBookings ?? false;

				this.setNavColors(configs.primary_nav_color, configs.primary_nav_text_color);
				this.setReceiptUrl(env, tokens, configs, tenant);

				// Based on configs, determine if the contractURL should be constructed
				if (configs?.contract_viewer?.enable_new_contract_viewer) {
					this.showContractViewerComponent = true;
				} else {
					this.showContractViewerComponent = false;
					this.setContractViewerUrl(user, env, configs);
				}
				if (data.orderTicketType && Object.keys(data.orderTicketType)?.length !== 0) {
					this.orderTicketTypes = data.orderTicketType;
				}
				if (validString(configs.supported_languages)) {
					this.supportedLanguages = configs.supported_languages.split(',').map((lang: string) => {
						return {
							name: lang,
							locale: lang.replace(/-|\s/gi, '_'),
						};
					});
				} else {
					this.supportedLanguages = [];
				}
				if (validArray(configs.navigation_tree_display)) {
					this.navigationTreeDisplay = configs.navigation_tree_display;
				}
			});
	}

	/**
	 * on init, sometimes contractViewerDetails takes longer to load
	 * this is the ultimate source if the tab should be displayed
	 */
	public ngOnInit(): void {
		this._store$.select(selectContractViewerDetails).subscribe((data) => {
			this.showContractViewerComponent = !!data;
		});
	}

	/**
	 * reset nav items on onChanges
	 */
	public ngOnChanges(): void {
		const displayContract =
			(this.orderTicketTypes.isMembership || this.orderTicketTypes.isPaymentPlan) &&
			(!!this.contractViewerUrl || this.showContractViewerComponent);
		const displayReceipt = !this.disableReceiptsNavItem && !!this.receiptUrl;
		const displayHistory =
			!!!this.disablePaymentHistoryTab &&
			this.hasPaymentHistory &&
			(this.orderTicketTypes.isMembership || this.orderTicketTypes.isPaymentPlan);

		const displayDocumentsParent = displayContract || displayReceipt || displayHistory;

		this.navItems = [
			{
				type: 'button',
				label: this._locale.get('nav.overview'),
				clickAction: 'routeTo',
				clickActionPayload: ['account', 'overview'],
				displayIf: this.orderTicketTypes.isMembership || this.orderTicketTypes.isPaymentPlan,
				analyticsClick: 'navigation|{"page":"overview"}',
				routeEnd: 'overview',
				id: 'overview',
			},
			{
				type: 'button',
				label: this._locale.get('nav.membership'),
				clickAction: 'routeTo',
				clickActionPayload: ['account', 'membership'],
				displayIf: this.orderTicketTypes.isMembership && !!!this.disableMembershipTab,
				analyticsClick: 'navigation|{"page":"membership"}',
				routeEnd: 'membership',
				id: 'membership',
			},
			{
				type: 'dropdown',
				dropDownName: this._locale.get('nav.payments'),
				displayIf:
					(this.orderTicketTypes.isMembership || this.orderTicketTypes.isPaymentPlan) &&
					!!!this.disableManagePaymentsTab,
				gapOpen: this.gapOpenState.payments,
				id: 'payments',
				buttons: [
					{
						type: 'button',
						label: this._locale.get('nav.payment'),
						clickAction: 'routeTo',
						clickActionPayload: ['account', 'payment'],
						displayIf:
							(this.orderTicketTypes.isMembership || this.orderTicketTypes.isPaymentPlan) &&
							!!!this.disableManagePaymentsTab,
						analyticsClick: 'navigation|{"page":"manage payments"}',
						routeEnd: 'payment',
						id: 'manage-payments',
					},
				],
			},
			{
				type: 'dropdown',
				dropDownName: this._locale.get('nav.documents'),
				displayIf: displayDocumentsParent,
				gapOpen: this.gapOpenState.documents,
				id: 'documents',
				buttons: [
					{
						type: 'button',
						label: this._locale.get('nav.receipt'),
						clickAction: 'navigateTo',
						clickActionPayload: [this.receiptUrl, '_blank'],
						displayIf: displayReceipt,
						analyticsClick: 'navigation|{"page":"receipt"}',
						routeEnd: 'receipt',
						id: 'receipt',
					},
					{
						type: 'button',
						label: this._locale.get('nav.history'),
						clickAction: 'routeTo',
						clickActionPayload: ['account', 'history'],
						displayIf: displayHistory,
						analyticsClick: 'navigation|{"page":"payment history"}',
						routeEnd: 'history',
						id: 'history',
					},
					{
						type: 'button',
						label: this._locale.get('nav.contract'),
						clickAction: 'navigateTo',
						clickActionPayload: [this.contractViewerUrl, '_blank'],
						displayIf: displayContract && !this.showContractViewerComponent,
						analyticsClick: 'navigation|{"page":"legacy view contract"}',
						routeEnd: 'contract',
						id: 'contract',
					},
					{
						type: 'button',
						label: this._locale.get('nav.contract'),
						clickAction: 'routeTo',
						clickActionPayload: ['account', 'contract-viewer'],
						displayIf: displayContract && this.showContractViewerComponent,
						analyticsClick: 'navigation|{"page":"contract-viewer"}',
						routeEnd: 'contract-viewer',
						id: 'contract-viewer',
					},
				],
			},
			{
				type: 'button',
				label: this._locale.get('nav.bookingPortal'),
				clickAction: 'routeTo',
				clickActionPayload: ['booking-portal', 'summary'],
				displayIf: this.orderTicketTypes.isBookable,
				analyticsClick: 'navigation|{"page":"booking-portal"}',
				routeEnd: 'booking-portal',
				id: 'booking-portal',
			},
			{
				type: 'button',
				label: this._locale.get('nav.faq'),
				clickAction: 'routeTo',
				clickActionPayload: ['account', 'faq'],
				displayIf: !!!this.disableFaqTab,
				analyticsClick: 'navigation|{"page":"faq"}',
				routeEnd: 'faq',
				id: 'faq',
			},
			{
				type: 'button',
				label: this._locale.get('nav.logout'),
				clickAction: 'logout',
				clickActionPayload: '',
				displayIf: true,
				analyticsClick: 'navigation|{"page":"logout"}',
				routeEnd: 'logout',
				id: 'logout',
			},
		];

		if (!!this.navigationTreeDisplay) {
			this.navItems = this.sortNavItems(this.navItems, this.navigationTreeDisplay, 'id');
		}
	}

	/**
	 * Sorts the navItems array based on the order from the navigation_tree_display config
	 * navigation_tree_display uses the id of the navItems
	 * sorting is done by comparing the index of the id in the navigation_tree_display and navItems id
	 * @param navItems Original navItems array
	 * @param order Order to sort navItems by
	 * @param key id
	 * @returns sorted navItems array
	 */
	public sortNavItems(navItems, order, key): Array<NavItem> {
		const sortedNavItems = navItems.sort((a, b) => {
			let A = a[key],
				B = b[key];

			if (order.indexOf(A) > order.indexOf(B)) {
				return 1;
			} else {
				return -1;
			}
		});
		return sortedNavItems;
	}

	/**
	 * Sets the background and foreground color for nav bar.
	 * If the color is not defined, this sets a default value as a "transparent" bg and a black fg
	 * Currently not being used, but it is likely we will allow client customization in the future.
	 * @param bg The background color
	 * @param fg The foreground text color
	 */
	public setNavColors(bg: string, fg: string): void {
		this.primaryNavColor = bg ?? DEFAULT_NAV_BACKGROUND_COLOR;
		this.primaryNavTextColor = fg ?? DEFAULT_NAV_FOREGROUND_COLOR;
	}
	/**
	 * Sets the contract viewer url if configured.
	 * @param user The user
	 * @param env The environment
	 * @param configs The selected app configs
	 */
	public setContractViewerUrl(user: User, env: EnvironmentState, configs): void {
		if (!user || !configs.contract_viewer?.enable || !user.details) {
			return;
		}

		const [details] = user.details;

		if (details.phone) {
			this.contractViewerUrl = `https://${env.subDomainPrefix}contractviewer${env.subDomainSuffix}.${
				env.domain
			}/${configs.contract_viewer.path ?? 'unknown'}/view/phoneNumber:${details.phone[0]?.number}/orderID:${
				user.orderNumber
			}`;
		} else {
			// there isn't enought user information
			return;
		}
	}
	/**
	 * Sets the receipt url
	 * @param env The environment data
	 * @param tokens The order tokens
	 * @param configs Application configs
	 * @param tenant The tenant name
	 */
	public setReceiptUrl(env: EnvironmentState, tokens: Tokens, configs: ApplicationConfigs, tenant: string) {
		const island = window.location.pathname.split('/');
		this.receiptUrl = `https://receipt${env.subDomainSuffix}.${env.domain}/${this.getReceiptTenant(
			tenant,
			configs
		)}/receipt.php?oToken=${tokens.oToken}&${
			validString(tokens.pToken) ? 'pToken=' + tokens.pToken : 'cToken=' + tokens.cToken
		}&island=${island[1]}`;
	}
	/**
	 * Returns the tenant override
	 * @param tenant The tenant name
	 * @param configs The selected app configs
	 */
	public getReceiptTenant(tenant: string, configs: ApplicationConfigs): string {
		const lowercaseTenant = toLower(tenant);
		const receipt_tenant = configs?.receipts?.receipt_tenant;

		if (validString(receipt_tenant)) {
			return receipt_tenant;
		}

		switch (lowercaseTenant) {
			case 'czv3':
				return 'columbuszoo';
			default:
				return tenant;
		}
	}
	/**
	 * fire off appropriate action on click
	 * @param action the type of action
	 * @param payload the payload to pass to the action
	 */
	public clickAction(action: string, payload?: string[]): void {
		switch (action) {
			case 'routeTo':
				this.routeToPage(payload);
				break;
			case 'navigateTo':
				this.navigateToPage(payload[0], payload[1]);
				break;
			case 'logout':
				this.logout();
				break;
		}
	}
	/**
	 * Routes to a specific page within AHP and toggles the nav closed
	 * @param params The url values needed to route
	 */
	public routeToPage(params): void {
		this._store$.dispatch(routeTo({ payload: params }));
		this.closeMobileNav();
	}
	/**
	 * Opens a new tab for documents and toggles the nav closed
	 * @param url The url values needed to route to
	 * @param location where the url opens
	 */
	public navigateToPage(url: string, location: string) {
		window.open(url, location);
		this.closeMobileNav();
	}
	/**
	 * sets isMobileOpen to false and emits the closeMobile event so that the
	 * parent nav knows to play the 'hide' animation.
	 */
	public closeMobileNav() {
		this.isMobileOpen = false;
		this.closeMobile.emit();
	}
	/**
	 * toggles a given nav item
	 * @param navItem
	 */
	public toggleNavDropdown(navItem: { dropDownName: string }): void {
		this.gapOpenState[navItem.dropDownName.toLowerCase()] = !this.gapOpenState[navItem.dropDownName.toLowerCase()];
	}
	/**
	 * set the selected indicator for the nav items
	 * @param navButton
	 */
	public setIsSelected(navButton: string): boolean {
		return this._router.url.split('/').includes(navButton);
	}
	/**
	 * Logs the user out.
	 */
	public logout(): void {
		this._store$.dispatch(logout());
	}
	/**
	 * Called when destroying the instance.
	 */
	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}
}
